import { useState } from 'react';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { TextareaField } from '@/kit/FormField';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { CancelButton } from '@/features/tr-workflow/common';

/** What the dialog resolves with: the reason the user gave (it goes into the audit entry), or nothing when it is cancelled. */
export interface UnfinaliseDialogData {
  /** The period shown in the title, e.g. '01-Jan-2025 to 31-Dec-2025'. */
  periodLabel: string;
}

export const UNFINALISE_REASON_MAX = 500;

/**
 * APT336 (the client, 30 Oct): "once I finalise the accounts ... the stage continues to show Finalised, and I can't find an option to change the status.
 * Could you please guide me on how to revert the accounts from Finalised back to Approved or Review stage?" Finalised accounts go back to Approved,
 * with a reason. The text says what changes and what does not.
 */
export function UnfinaliseDialog({ data, dialogRef }: DialogComponentProps<UnfinaliseDialogData, string>) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();

  return (
    <div className="mat-dialog-wrapper new-account unfinalise-dialog">
      <DialogContent>
        <div className="main-contain">
          <div className="sidenav-header">
            <div className="d-flex align-items-center justify-space-between">
              <h2 className="m-0">
                <span>Unfinalise accounts{data?.periodLabel ? ` - ${data.periodLabel}` : ''}</span>
              </h2>
              <DialogClose>
                <Icon name="close" className="close" />
              </DialogClose>
            </div>
          </div>
        </div>

        <div className="sidenav-body">
          <p role="note" data-testid="unfinalise-explanation">
            The accounts go back to <strong>Approved</strong>. The approved version stays approved, but it is no longer final, so you can change the period
            and then approve and finalise it again.
          </p>
          <ul className="text-gray font-size-12" data-testid="unfinalise-details">
            <li>If the figures change, the approved version goes out of date: generate and approve it again before you finalise.</li>
            <li>Accounts that are with the client for approval stay as they are; clear that first if you need to change the inputs.</li>
            <li>The lock on Accounting Periods is not changed.</li>
            <li>Accounts that have been submitted or filed cannot be unfinalised; they are changed by a revision.</li>
          </ul>

          <div className="form-field textarea">
            <p className="fw-bold">
              Reason <span className="text-red">*</span>
            </p>
            <TextareaField
              fieldClassName="wp-100"
              rows={6}
              name="unfinaliseReason"
              maxLength={UNFINALISE_REASON_MAX}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>

        <div className="sidenav-footer">
          <div className="d-flex align-items-center gap-10">
            <Button className="action-button primary-button" disabled={trimmed.length === 0} onClick={() => dialogRef.close(trimmed)}>
              Unfinalise accounts
            </Button>
            <CancelButton />
          </div>
        </div>
      </DialogContent>
    </div>
  );
}
